<script setup>
import { ref, computed } from 'vue';
import { Head, router, useForm, usePage } from '@inertiajs/vue3';
import {
    PhArrowLeft, PhCheckCircle, PhXCircle,
    PhMicrophone, PhFileText,
} from '@phosphor-icons/vue';
import Layout   from '@pages/Shared/layouts/AdminLayout.vue';
import Modal    from '@pages/Shared/Modal.vue';
import Select   from '@pages/Shared/form/Select.vue';
import Textarea from '@pages/Shared/form/Textarea.vue';
import Input    from '@pages/Shared/form/Input.vue';
import Spinner  from '@pages/Shared/ui/Spinner.vue';
import { flashToast } from '@/toast.js';

const props = defineProps({
    application: Object,
    sections:    Array,
});

const page = usePage();
const can  = computed(() => page.props.can || {});

// Flat class options from sections → classes (used in Admit modal)
const classOptions = computed(() =>
    (props.sections ?? []).flatMap(s =>
        (s.classes ?? []).map(c => ({ label: `${c.name} (${s.name})`, value: c.id }))
    )
);

// ── Schedule Interview ─────────────────────────────────────────────────────
// Shown when status = submitted and no interview exists yet.
const scheduleModal = ref(false);
const scheduleForm  = useForm({ scheduled_at: '', venue: '', invitation_note: '' });
const submitSchedule = () => {
    scheduleForm.post(`/admin/applications/${props.application.id}/interview/schedule`, {
        onSuccess: (page) => {
            scheduleModal.value = false;
            scheduleForm.reset();
            flashToast(page.props.flash);
        },
    });
};

// ── Admit ──────────────────────────────────────────────────────────────────
// Only available after interview is fully recorded (status = interviewed).
const admitModal = ref(false);
const admitForm  = useForm({ class_room_id: null, notes: '' });
const submitAdmit = () => {
    admitForm.post(`/admin/applications/${props.application.id}/admit`, {
        onSuccess: (page) => {
            admitModal.value = false;
            flashToast(page.props.flash);
        },
    });
};

// ── Reject ─────────────────────────────────────────────────────────────────
const rejectModal = ref(false);
const rejectForm  = useForm({ rejection_reason: '' });
const submitReject = () => {
    rejectForm.post(`/admin/applications/${props.application.id}/reject`, {
        onSuccess: (page) => {
            rejectModal.value = false;
            flashToast(page.props.flash);
        },
    });
};

const statusColor = (status) => ({
    draft:       'bg-gray-100 text-gray-600',
    submitted:   'bg-primary-50 text-primary-700',
    invited:     'bg-indigo-50 text-indigo-700',
    interviewed: 'bg-purple-50 text-purple-700',
    admitted:    'bg-green-50 text-green-700',
    rejected:    'bg-red-50 text-red-600',
    enrolled:    'bg-emerald-50 text-emerald-700',
}[status] ?? 'bg-gray-100 text-gray-600');

const app       = computed(() => props.application);
const applicant = computed(() => app.value.applicant);
</script>

<template>
    <Layout>
        <Head :title="`Application — ${applicant?.first_name} ${applicant?.last_name}`" />

        <!-- ── Schedule Interview Modal ─────────────────────────────────── -->
        <Modal v-model="scheduleModal" title="Schedule Interview" @close="scheduleModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitSchedule">
                <Input
                    label="Date & Time"
                    v-model="scheduleForm.scheduled_at"
                    type="datetime-local"
                    :error="scheduleForm.errors.scheduled_at"
                    class="mb-4"
                />
                <Input
                    label="Venue (optional)"
                    v-model="scheduleForm.venue"
                    type="text"
                    placeholder="e.g. Admin Office, Room 1"
                    class="mb-4"
                />
                <Textarea
                    label="Invitation Note (optional)"
                    v-model="scheduleForm.invitation_note"
                    :rows="3"
                    class="mb-4"
                />
                <button
                    type="submit"
                    :disabled="scheduleForm.processing"
                    class="w-full py-3 rounded-lg bg-primary hover:bg-primary-light text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition"
                >
                    <Spinner v-if="scheduleForm.processing" :size="14" />
                    Schedule Interview
                </button>
            </form>
        </Modal>

        <!-- ── Admit Modal ──────────────────────────────────────────────── -->
        <Modal v-model="admitModal" title="Grant Admission" @close="admitModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitAdmit">
                <p class="text-sm text-gray-500 mb-4">
                    Applied for section: <strong>{{ app.section?.name }}</strong>.
                    Select the class to admit into:
                </p>
                <Select
                    label="Admit into Class"
                    v-model="admitForm.class_room_id"
                    :options="classOptions"
                    placeholder="Select class"
                    :error="admitForm.errors.class_room_id"
                    class="mb-4"
                />
                <Textarea
                    label="Notes (optional)"
                    v-model="admitForm.notes"
                    :rows="3"
                    class="mb-4"
                />
                <button
                    type="submit"
                    :disabled="admitForm.processing"
                    class="w-full py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition"
                >
                    <Spinner v-if="admitForm.processing" :size="14" />
                    Confirm Admission
                </button>
            </form>
        </Modal>

        <!-- ── Reject Modal ─────────────────────────────────────────────── -->
        <Modal v-model="rejectModal" title="Reject Application" @close="rejectModal = false">
            <form class="p-4 md:p-5" @submit.prevent="submitReject">
                <Textarea
                    label="Rejection Reason"
                    v-model="rejectForm.rejection_reason"
                    :rows="4"
                    :error="rejectForm.errors.rejection_reason"
                    class="mb-4"
                />
                <button
                    type="submit"
                    :disabled="rejectForm.processing"
                    class="w-full py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white
                           text-sm font-semibold flex justify-center items-center gap-2 transition"
                >
                    <Spinner v-if="rejectForm.processing" :size="14" />
                    Confirm Rejection
                </button>
            </form>
        </Modal>

        <!-- ── Back + Header ─────────────────────────────────────────────── -->
        <div class="flex items-center gap-3 mb-6">
            <button
                @click="router.get('/admin/applications')"
                class="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition"
            >
                <PhArrowLeft :size="20" />
            </button>
            <div class="flex-1">
                <h2 class="text-xl font-bold text-primary">Application Detail</h2>
                <p class="text-sm text-gray-500">{{ applicant?.first_name }} {{ applicant?.last_name }}</p>
            </div>
            <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize"
                :class="statusColor(app.status)"
            >
                {{ app.status }}
            </span>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <!-- ── Left ─────────────────────────────────────────────────── -->
            <div class="lg:col-span-1 space-y-4">

                <!-- Applicant card -->
                <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-3">
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Applicant</h4>
                    <p class="font-semibold text-gray-900">
                        {{ applicant?.first_name }} {{ applicant?.other_name }} {{ applicant?.last_name }}
                    </p>
                    <p class="text-xs text-gray-500">{{ applicant?.id }}</p>
                    <div class="text-sm text-gray-600 space-y-1.5 pt-1">
                        <p>📧 {{ applicant?.email }}</p>
                        <p>📱 {{ applicant?.phone ?? '—' }}</p>
                        <p>📅 DOB: {{ applicant?.dob ?? '—' }}</p>
                        <p>🏠 {{ applicant?.address ?? '—' }}</p>
                    </div>
                </div>

                <!-- Application info -->
                <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-2">
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Application Info</h4>
                    <div class="text-sm text-gray-600 space-y-1.5">
                        <p><span class="text-gray-400">Session:</span> {{ app.academic_session?.name }}</p>
                        <p><span class="text-gray-400">Section:</span> {{ app.section?.name ?? '—' }}</p>
                        <p v-if="app.admission?.class_room">
                            <span class="text-gray-400">Admitted Class:</span>
                            {{ app.admission.class_room.name }}
                        </p>
                        <p><span class="text-gray-400">Previous School:</span> {{ app.previous_school ?? '—' }}</p>
                        <p>
                            <span class="text-gray-400">Submitted:</span>
                            {{ app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : 'Not submitted' }}
                        </p>
                    </div>
                </div>

                <!-- Documents -->
                <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Documents</h4>
                    <div v-if="app.documents?.length" class="space-y-2">
                        <div v-for="doc in app.documents" :key="doc.id"
                            class="flex items-center justify-between text-sm">
                            <div class="flex items-center gap-2 text-gray-600">
                                <PhFileText :size="16" class="text-gray-400" />
                                <span class="capitalize">{{ doc.type?.replace(/_/g, ' ') }}</span>
                            </div>
                            <span :class="doc.is_verified
                                ? 'text-green-600 text-xs font-medium'
                                : 'text-yellow-600 text-xs font-medium'">
                                {{ doc.is_verified ? 'Verified' : 'Pending' }}
                            </span>
                        </div>
                    </div>
                    <p v-else class="text-xs text-gray-400">No documents uploaded.</p>
                </div>
            </div>

            <!-- ── Right ─────────────────────────────────────────────────── -->
            <div class="lg:col-span-2 space-y-5">

                <!-- Actions -->
                <div class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Actions</h4>
                    <div class="flex flex-wrap gap-3">

                        <!-- Schedule: submitted + no interview yet -->
                        <button
                            v-if="app.status === 'submitted' && !app.interview"
                            @click="scheduleModal = true"
                            class="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600
                                   hover:bg-indigo-700 text-white text-sm font-semibold transition"
                        >
                            <PhMicrophone :size="16" />
                            Schedule Interview
                        </button>

                        <!--
                            Interview in progress indicator — no actions here.
                            Record outcome and reschedule are done on the Interviews page.
                        -->
                        <div
                            v-if="app.interview && !['completed'].includes(app.interview?.status) && app.status === 'invited'"
                            class="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-50
                                   text-indigo-700 text-sm font-medium"
                        >
                            <PhMicrophone :size="16" />
                            Interview scheduled — manage on
                            <button
                                @click="router.get('/admin/interviews')"
                                class="underline font-semibold hover:text-indigo-900 transition"
                            >
                                Interviews page
                            </button>
                        </div>

                        <!-- Admit: only after interview is recorded (status = interviewed) -->
                        <button
                            v-if="app.status === 'interviewed'"
                            @click="admitModal = true"
                            class="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600
                                   hover:bg-green-700 text-white text-sm font-semibold transition"
                        >
                            <PhCheckCircle :size="16" />
                            Admit
                        </button>

                        <!-- Reject: any active stage -->
                        <button
                            v-if="!['rejected', 'enrolled', 'admitted'].includes(app.status)"
                            @click="rejectModal = true"
                            class="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600
                                   hover:bg-red-700 text-white text-sm font-semibold transition"
                        >
                            <PhXCircle :size="16" />
                            Reject
                        </button>

                        <!-- View Admission -->
                        <button
                            v-if="app.admission"
                            @click="router.get(`/admin/admissions/${app.admission.id}`)"
                            class="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary
                                   hover:bg-primary-light text-white text-sm font-semibold transition"
                        >
                            View Admission
                        </button>
                    </div>
                </div>

                <!-- Interview details (read-only — actions are on Interviews page) -->
                <div v-if="app.interview" class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
                    <div class="flex items-center justify-between mb-3">
                        <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Interview</h4>
                        <button
                            v-if="['scheduled', 'rescheduled', 'absent'].includes(app.interview.status)"
                            @click="router.get('/admin/interviews')"
                            class="text-xs text-indigo-600 hover:underline font-semibold"
                        >
                            Manage on Interviews page →
                        </button>
                    </div>
                    <div class="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>
                            <p class="text-gray-400 text-xs mb-0.5">Status</p>
                            <span class="capitalize font-medium">{{ app.interview.status }}</span>
                        </div>
                        <div>
                            <p class="text-gray-400 text-xs mb-0.5">Scheduled</p>
                            <p>{{ app.interview.scheduled_at
                                ? new Date(app.interview.scheduled_at).toLocaleString()
                                : '—' }}</p>
                        </div>
                        <div>
                            <p class="text-gray-400 text-xs mb-0.5">Venue</p>
                            <p>{{ app.interview.venue ?? '—' }}</p>
                        </div>
                        <div>
                            <p class="text-gray-400 text-xs mb-0.5">Score</p>
                            <p>
                                {{ app.interview.score ?? '—' }}
                                <span v-if="app.interview.score">/100</span>
                            </p>
                        </div>
                        <div v-if="app.interview.remarks" class="col-span-2">
                            <p class="text-gray-400 text-xs mb-0.5">Remarks</p>
                            <p>{{ app.interview.remarks }}</p>
                        </div>
                        <div v-if="app.interview.conductor">
                            <p class="text-gray-400 text-xs mb-0.5">Conducted by</p>
                            <p>{{ app.interview.conductor?.name }}</p>
                        </div>
                    </div>
                </div>

                <!-- Rejection reason -->
                <div v-if="app.status === 'rejected'"
                    class="bg-red-50 border border-red-100 rounded-xl p-5">
                    <h4 class="text-sm font-semibold text-red-700 mb-2">Rejection Reason</h4>
                    <p class="text-sm text-red-600">{{ app.rejection_reason }}</p>
                </div>

                <!-- Payments -->
                <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div class="px-5 py-4 border-b border-gray-100">
                        <h4 class="font-semibold text-gray-800">Payments</h4>
                    </div>
                    <div v-if="app.payments?.length" class="divide-y divide-gray-100">
                        <div v-for="payment in app.payments" :key="payment.id"
                            class="px-5 py-4 flex items-center justify-between text-sm">
                            <div>
                                <p class="font-medium text-gray-800 capitalize">
                                    {{ payment.category?.replace(/_/g, ' ') }}
                                </p>
                                <p class="text-xs text-gray-400">{{ payment.reference }}</p>
                            </div>
                            <div class="text-right">
                                <p class="font-semibold text-gray-900">
                                    ₦{{ Number(payment.amount_paid).toLocaleString() }}
                                </p>
                                <span class="text-xs capitalize"
                                    :class="payment.status === 'paid' ? 'text-green-600' : 'text-yellow-600'">
                                    {{ payment.status }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div v-else class="px-5 py-8 text-center text-sm text-gray-400">
                        No payments recorded.
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>